import { useEffect, useState } from "react";
import { feedProductionService } from "@/services/feedProductionService";
import { handleApiError } from "@/services/api";
import { formatINR } from "@/lib/utils";
import { formatQty, displayUnit } from "@/utils/formatQty";
import type { BatchPlan } from "@/types/models";

// What this batch will take from stock and cost, before it is produced — the
// same plan the server uses to produce it (feed_production_service.go:
// planBatch), so the figures here are the figures the batch gets.
export default function BatchPreview({ formulaId, batchSizeKg }: { formulaId: string; batchSizeKg: string }) {
  const [plan, setPlan] = useState<BatchPlan | null>(null);
  const [error, setError] = useState("");

  const id = parseInt(formulaId);
  const kg = parseFloat(batchSizeKg);
  const ready = id > 0 && kg > 0;

  useEffect(() => {
    if (!ready) {
      setPlan(null);
      setError("");
      return;
    }
    let cancelled = false;
    // Wait for the typing to stop before asking.
    const timer = setTimeout(() => {
      feedProductionService
        .preview(id, kg)
        .then((p) => {
          if (cancelled) return;
          setPlan(p);
          setError("");
        })
        .catch((err) => {
          if (cancelled) return;
          setPlan(null);
          setError(handleApiError(err));
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ready, id, kg]);

  if (!ready) return null;
  if (error) {
    return <p className="text-sm text-error-600 dark:text-error-500">{error}</p>;
  }
  if (!plan) {
    return <p className="text-sm text-gray-400">Working out the batch…</p>;
  }

  const short = (plan.lines ?? []).filter((l) => l.short);
  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-800" data-testid="batch-preview">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">This batch will use</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
            <th className="pb-1 font-normal">Material</th>
            <th className="pb-1 text-right font-normal">Needed</th>
            <th className="pb-1 text-right font-normal">In stock</th>
            <th className="pb-1 text-right font-normal">Cost</th>
          </tr>
        </thead>
        <tbody>
          {(plan.lines ?? []).map((l) => (
            <tr key={l.raw_material_id} className="border-t border-gray-100 dark:border-gray-800">
              <td className="py-1 text-gray-800 dark:text-white/90">{l.name}</td>
              <td className="py-1 text-right text-gray-800 dark:text-white/90">
                {formatQty(l.qty, l.unit)} {displayUnit(l.unit)}
              </td>
              <td className={`py-1 text-right ${l.short ? "font-medium text-error-600 dark:text-error-500" : "text-gray-500 dark:text-gray-400"}`}>
                {formatQty(l.in_stock, l.unit)} {displayUnit(l.unit)}
              </td>
              <td className="py-1 text-right text-gray-500 dark:text-gray-400">
                {l.cost !== null ? formatINR(l.cost, 0) : l.feed_ingredient ? "no rate" : "not in feed cost"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-sm text-gray-800 dark:text-white/90">
        {plan.total_cost !== null && plan.cost_per_kg !== null
          ? `Feed cost ${formatINR(plan.total_cost, 0)} · ${formatINR(plan.cost_per_kg, 2)}/kg`
          : "Feed cost not known yet — no purchase rate on file for these ingredients."}
      </p>
      {short.length > 0 && (
        <p className="mt-1 text-xs text-warning-600 dark:text-warning-400">
          Not enough stock of {short.map((l) => l.name).join(", ")} — the batch can still be produced, and stock will go below zero.
        </p>
      )}
    </div>
  );
}
